import { type JSX } from 'react';

import { Button } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FieldLabel } from 'Components/FieldLabel/FieldLabel';
import { Modal } from 'Components/Modal/Modal';
import { TagListing } from 'Components/Tags/TagListing/TagListing';
import { Text } from 'Components/Text/Text';
import { ICON_CLOSE } from 'Config/Icons';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { RiskHistory } from 'Models/RiskRegister';

import styles from './AssessmentRiskSnapshotModal.module.css';

export interface AssessmentRiskSnapshotModalProps {
    assessmentTimestamp: string;
    hideModal: () => void;
    riskHistory: RiskHistory;
    submittedBy: string;
}

export const AssessmentRiskSnapshotModal = (props: AssessmentRiskSnapshotModalProps): JSX.Element => {
    const cachedData = useCachedData();

    const reviewerName = getUserNameFromSubject(props.submittedBy, cachedData.users, UserNameFormat.FIRST_SPACE_LAST);
    const reviewDate = iso8601ToUsDateShort(props.assessmentTimestamp);

    return (
        <Modal open header={`${props.riskHistory.category.title} Risk Review`} subHeader={`Submitted ${reviewDate} by ${reviewerName}`}>
            <div className={styles.textSection}>
                <div>
                    <FieldLabel fieldName="Title" />
                    <Text noStyles>{props.riskHistory.title}</Text>
                </div>
                <div>
                    <FieldLabel fieldName="Strategy" />
                    <Text noStyles>{props.riskHistory.strategy ? props.riskHistory.strategy.title : '-'}</Text>
                </div>
                <div>
                    <FieldLabel fieldName="Owner" />
                    <Text noStyles>{props.riskHistory.owner ? getUserNameFromSubject(props.riskHistory.owner, cachedData.users, UserNameFormat.FIRST_SPACE_LAST) : '-'}</Text>
                </div>
            </div>
            <div className={styles.textSection}>
                <div>
                    <FieldLabel fieldName="Description" />
                    <Text noStyles>{props.riskHistory.description}</Text>
                </div>
            </div>
            <div className={styles.textSection}>
                <div>
                    <FieldLabel fieldName="Tags" />
                    {props.riskHistory.tags.length > 0 ? <TagListing tags={props.riskHistory.tags} showAll /> : <Text noStyles>-</Text>}
                </div>
            </div>
            <div className={styles.textSection}>
                <div>
                    <FieldLabel fieldName="Treatment Plan Description" />
                    <Text noStyles>{props.riskHistory.treatment_plan_description ? props.riskHistory.treatment_plan_description : '-'}</Text>
                </div>
            </div>
            <div className={styles.scoresSection}>
                <div>
                    <FieldLabel fieldName="Initial Impact" />
                    <Text noStyles>{props.riskHistory.inherent_risk_impact}</Text>
                </div>
                <div>
                    <FieldLabel fieldName="Initial Probability" />
                    <Text noStyles>{props.riskHistory.inherent_risk_probability}</Text>
                </div>
                <div>
                    <FieldLabel fieldName="Total Inherent Risk" />
                    <Text noStyles>{props.riskHistory.total_inherent_risk}</Text>
                </div>
            </div>
            <div className={styles.scoresSection}>
                <div>
                    <FieldLabel fieldName="Current Impact" />
                    <Text noStyles>{props.riskHistory.current_risk_impact}</Text>
                </div>
                <div>
                    <FieldLabel fieldName="Current Probability" />
                    <Text noStyles>{props.riskHistory.current_risk_probability}</Text>
                </div>
                <div>
                    <FieldLabel fieldName="Total Current Risk" />
                    <Text noStyles>{props.riskHistory.total_current_risk}</Text>
                </div>
            </div>
            <div className={styles.scoresSection}>
                <div>
                    <FieldLabel fieldName="Target Impact" />
                    <Text noStyles>{props.riskHistory.target_risk_impact}</Text>
                </div>
                <div>
                    <FieldLabel fieldName="Target Probability" />
                    <Text noStyles>{props.riskHistory.target_risk_probability}</Text>
                </div>
                <div>
                    <FieldLabel fieldName="Total Target Risk" />
                    <Text noStyles>{props.riskHistory.total_target_risk}</Text>
                </div>
            </div>
            <div className={styles.buttonContainer}>
                <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                    Close
                </Button>
            </div>
        </Modal>
    );
};
